import { Permissions } from "@repo/database";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import type { CartItem } from "~/providers/cart-provider";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { useCartStore } from "~/stores/use-cart-store";
import { usePermissionManager } from "./use-permission-manager";

export const useCartManager = () => {
  const { t } = useTranslation("common");

  const { setUnsavedChanges, checkUnsavedChanges } = useUnsavedChanges();

  const { hasPermission } = usePermissionManager();

  const navigate = useNavigate();

  const {
    setMiscProductOpen,
    setOrderCommentDialogOpen,
    setOrderDiscountDialogOpen,
    setOrderContextOpen,
    addItem,
    reset,
    setSearchInput,
    removeItem,
    getNewItems,
  } = useCartStore(
    useShallow((state) => ({
      setMiscProductOpen: state.setMiscProductOpen,
      setOrderCommentDialogOpen: state.setOrderCommentDialogOpen,
      setOrderDiscountDialogOpen: state.setOrderDiscountDialogOpen,
      addItem: state.addItem,
      removeItem: state.removeItem,
      reset: state.reset,
      setSearchInput: state.setSearchInput,
      setOrderContextOpen: state.setOrderContextOpen,
      getNewItems: state.getNewItems,
    })),
  );

  const handleReset = () => {
    reset();
    setUnsavedChanges({ isDirty: false });
  };

  const handleCancelOperation = () => {
    if (checkUnsavedChanges() === false) {
      void navigate({ to: "/floor-plan" });
    }
  };

  const handleAddToCart = async (item: CartItem) => {
    const { employee, permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
      label: t("Permissions.add_to_cart_label"),
    });

    if (!permission) return false;

    addItem({
      ...item,
      employeeId: employee?.id,
    });

    setUnsavedChanges({
      isDirty: getNewItems().length > 0,
      onDiscard: handleReset,
    });

    setSearchInput("");

    return true;
  };

  const handleRemoveFromCart = async (item: CartItem, amount?: number) => {
    let employeeId: string | undefined;

    if (item.ordered === true) {
      const { permission, employee } = await hasPermission({
        requiredPermission: Permissions.delete_order_line,
      });

      if (!permission) return false;

      employeeId = employee?.id;
    } else {
      const { permission } = await hasPermission({
        requiredPermission: Permissions.update_order,
      });

      if (!permission) return false;
    }

    removeItem(
      {
        ...item,
        ordered: false,
        employeeId,
      },
      amount,
    );

    setUnsavedChanges({
      isDirty: getNewItems().length > 0,
      onDiscard: handleReset,
    });

    setSearchInput("");

    return true;
  };

  const openMiscProduct = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
    });

    if (!permission) return false;

    setMiscProductOpen(true);
  };

  const openOrderComment = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
    });

    if (!permission) return false;
    setOrderCommentDialogOpen(true);
  };

  const openOrderContext = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order,
    });

    if (!permission) return false;
    setOrderContextOpen(true);
  };

  const openOrderDiscount = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.discount_order,
    });

    if (!permission) return false;

    setOrderDiscountDialogOpen(true);
  };

  return {
    addToCart: handleAddToCart,
    removeFromCart: handleRemoveFromCart,
    openMiscProduct,
    openOrderDiscount,
    openOrderComment,
    openOrderContext,
    cancelOperation: handleCancelOperation,
  };
};
